/**
 * Oxirgi ochilgan driverlar (⌘K uchun): shu qurilmada, foydalanuvchi bo'yicha alohida kalit (bir brauzerda boshqa
 * xodim kirsa, oldingisining ro'yxatini ko'rmaydi), oxirgi 6 tasi, boshqa tablar `storage` hodisasi orqali ergashadi.
 */
import { useSyncExternalStore } from "react";

export interface RecentDriver {
  id: number;
  name: string;
  meta: string;
  status: string;
  at: number;
}

const PREFIX = "dh.recentDrivers.";
const MAX = 6;
const EMPTY: RecentDriver[] = [];
const listeners = new Set<() => void>();
const cache = new Map<string, RecentDriver[]>();

function keyFor(userId: number | null | undefined): string | null {
  return userId ? `${PREFIX}${userId}` : null;
}

function read(key: string | null): RecentDriver[] {
  if (!key) return EMPTY;
  const hit = cache.get(key);
  if (hit) return hit;
  let list: RecentDriver[] = EMPTY;
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as RecentDriver[]) : [];
    list = Array.isArray(parsed) ? parsed.filter((r) => typeof r?.id === "number" && typeof r?.name === "string") : EMPTY;
  } catch {
    list = EMPTY;
  }
  cache.set(key, list);
  return list;
}

function write(key: string, list: RecentDriver[]) {
  cache.set(key, list);
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* ixtiyoriy: maxfiy rejim yoki to'lgan xotira */
  }
  listeners.forEach((l) => l());
}

export function rememberDriver(d: { id: number; full_name: string; state?: string; status?: string; cdl_class?: string }, userId: number | null | undefined) {
  const key = keyFor(userId);
  if (!key) return;
  const meta = [d.state, d.cdl_class ? `Class ${d.cdl_class}` : ""].filter(Boolean).join(" · ");
  write(key, [{ id: d.id, name: d.full_name, meta, status: d.status ?? "", at: Date.now() }, ...read(key).filter((r) => r.id !== d.id)].slice(0, MAX));
}

export function forgetDriver(id: number, userId: number | null | undefined) {
  const key = keyFor(userId);
  if (!key) return;
  write(key, read(key).filter((r) => r.id !== id));
}

function subscribe(listener: () => void) {
  const onStorage = (e: StorageEvent) => {
    if (e.key?.startsWith(PREFIX)) {
      cache.delete(e.key);
      listener();
    }
  };
  listeners.add(listener);
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function useRecentDrivers(userId: number | null | undefined): RecentDriver[] {
  const key = keyFor(userId);
  return useSyncExternalStore(subscribe, () => read(key), () => EMPTY);
}
